// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import TransactionsTrendCard from "layouts/dashboard/components/TransactionsTrendCard";
import StatCard from "components/ui/StatCard";
import FilterBar from "components/ui/FilterBar";
import EmptyState from "components/ui/EmptyState";

// Data
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

import useApi from "../../components/Utils/api";
import { useEffect, useState } from "react";
import { DateRangePicker } from "rsuite";
import 'rsuite/dist/rsuite.min.css';
import { useMaterialUIController, setDashboardDataPs, setDefaultCalendar } from "../../context";
import subDays from "date-fns/subDays";
import { format } from "date-fns";
import usePermission from "components/Utils/usePermission";
import { formatNumber } from "../../utils";

const CURRENCY_COLORS = ["#2f7de1", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"];

const localDateString = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const generateDateRange = (startDate, endDate) => {
  const dates = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  while (current <= end) {
    dates.push(localDateString(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

function Dashboard() {
  const cp = usePermission();
  const { predefinedRanges } = reportsLineChartData;
  const [controller, dispatch] = useMaterialUIController();
  const { dashboardDataPs, defaultCalendarValue } = controller;
  const [selectedCurrency, setSelectedCurrency] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    transactionSummary: [],
    totalNumberOfTransactions: 0,
    totalTransactionsAmount: 0,
  });
  const { afterToday } = DateRangePicker;
  const api = useApi();

  const formatDate = (date) => {
    const d = new Date(date);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const getDashboardData = (dateRange) => {
    if (!dateRange) return;
    setDefaultCalendar(dispatch, dateRange);
    const data = {
      startDate: formatDate(dateRange[0]),
      endDate: formatDate(dateRange[1]),
    };
    api.post("transaction-summary-dashboard", data)
      .then((response) => {
        setDashboardData(response.data.data);
        setDashboardDataPs(dispatch, response.data.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!dashboardDataPs.transactionSummary || dashboardDataPs.transactionSummary.length === 0) {
      const lastMonthDateRange = [
        subDays(new Date(), 30),
        new Date(),
      ];
      getDashboardData(lastMonthDateRange);
    } else {
      setDashboardData(dashboardDataPs);
    }
  }, []);

  const summary = dashboardData?.transactionSummary || [];

  // Currencies present in this data set — derived dynamically
  const availableCurrencies = [...new Set(summary.map(r => r.currency != 'N/A' ? r.currency : "").filter(Boolean))].sort();

  const matchesStatus = (row) => {
    if (selectedStatus === "All") return true;
    if (selectedStatus === "COMPLETED") return ["COMPLETED", "AUTHORIZED"].includes(row.status);
    if (selectedStatus === "PENDING") return ["PENDING", "PENDING SUBSCRIBER VALIDATION"].includes(row.status);
    return row.status === selectedStatus;
  };

  // Status-only filter: all currencies kept so chart can render per-currency lines
  const statusFiltered = summary.filter(matchesStatus);

  // Full filter: used for stat card numbers
  const fullyFiltered = statusFiltered.filter(row =>
    selectedCurrency === "All" || row.currency === selectedCurrency
  );

  const totalCount = fullyFiltered.reduce((acc, row) => acc + (parseInt(row.totalNumOfTrans, 10) || 0), 0);
  const totalValue = formatNumber(fullyFiltered.reduce((acc, row) => acc + (parseFloat(row.amount) || 0), 0) / 100);

  const getStatusCount = (status) =>
    summary
      .filter(row => {
        const currencyMatch = selectedCurrency === "All" || row.currency === selectedCurrency;
        const statusMatch = status === "AUTHORIZED"
          ? ["COMPLETED", "AUTHORIZED"].includes(row.status)
          : row.status === status;
        return currencyMatch && statusMatch;
      })
      .reduce((acc, row) => acc + (parseInt(row.totalNumOfTrans, 10) || 0), 0);

  const convertTransactionToReport = (data, startDate, endDate, currencies) => {
    const labels = generateDateRange(new Date(startDate), new Date(endDate));

    // Aggregate by date + currency
    const aggregated = {};
    data.forEach(item => {
      const key = `${item.date}-${item.currency}`;
      if (!aggregated[key]) {
        aggregated[key] = { date: item.date, currency: item.currency, amount: 0, count: 0 };
      }
      aggregated[key].amount += parseFloat(item.amount) || 0;
      aggregated[key].count += parseInt(item.totalNumOfTrans, 10) || 0;
    });

    const makeDatasets = (type) =>
      currencies.map((currency, i) => {
        const values = Array(labels.length).fill(0);
        Object.values(aggregated).forEach(item => {
          if (item.currency !== currency) return;
          const idx = labels.indexOf(item.date);
          if (idx !== -1) {
            values[idx] = type === 'amount'
              ? parseFloat((item.amount / 100).toFixed(2))
              : item.count;
          }
        });
        return {
          label: currency,
          data: values,
          borderColor: CURRENCY_COLORS[i % CURRENCY_COLORS.length],
          backgroundColor: CURRENCY_COLORS[i % CURRENCY_COLORS.length],
          hidden: selectedCurrency !== "All" && selectedCurrency !== currency,
        };
      });

    return {
      amountTable: { labels, datasets: makeDatasets('amount') },
      countTable: { labels, datasets: makeDatasets('count') },
    };
  };

  const chartData = statusFiltered.length > 0 && availableCurrencies.length > 0 && defaultCalendarValue[0]
    ? convertTransactionToReport(statusFiltered, defaultCalendarValue[0], defaultCalendarValue[1], availableCurrencies)
    : null;

  const dateRangeLabel = defaultCalendarValue[0] && defaultCalendarValue[1]
    ? `${format(new Date(defaultCalendarValue[0]), "d MMM")} - ${format(new Date(defaultCalendarValue[1]), "d MMM yyyy")}`
    : "";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {cp("Dashboard", "Merchant") ? (
        <MDBox mt={3} pb={3}>
          {/* Header */}
          <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12} md="auto">
              <MDTypography variant="h5" fontWeight="medium">
                Dashboard
              </MDTypography>
            </Grid>
            <Grid item xs={12} md="auto">
              <MDButton
                variant={showFilters ? "gradient" : "outlined"}
                color="info"
                size="small"
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <Icon sx={{ mr: 0.5 }}>filter_list</Icon>
                Filters
              </MDButton>
            </Grid>
          </Grid>

          {showFilters && (
            <MDBox mt={2}>
              <FilterBar
                fields={[
                  {
                    name: "currency",
                    label: "Filter by Currency:",
                    type: "select",
                    options: availableCurrencies.map((c) => ({ value: c, label: c })),
                  },
                  {
                    name: "status",
                    label: "Filter By Status",
                    type: "select",
                    options: [
                      { value: "COMPLETED", label: "COMPLETED" },
                      { value: "FAILED", label: "FAILED" },
                      { value: "PENDING", label: "PENDING" },
                    ],
                  },
                ]}
                values={{
                  currency: selectedCurrency === "All" ? "" : selectedCurrency,
                  status: selectedStatus === "All" ? "" : selectedStatus,
                }}
                onChange={(name, value) => {
                  if (name === "currency") setSelectedCurrency(value || "All");
                  if (name === "status") setSelectedStatus(value || "All");
                }}
                onReset={() => {
                  setSelectedCurrency("All");
                  setSelectedStatus("All");
                }}
              >
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <DateRangePicker
                    disabledDate={afterToday()}
                    ranges={predefinedRanges}
                    onChange={(date) => getDashboardData(date)}
                    format="dd MMMM yyyy"
                    value={
                      Object.keys(defaultCalendarValue).length !== 0
                        ? [new Date(defaultCalendarValue[0]), new Date(defaultCalendarValue[1])]
                        : []
                    }
                  />
                </Grid>
              </FilterBar>
            </MDBox>
          )}

          {/* Stat cards */}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                color="dark"
                icon="bar_chart"
                label="Total Transactions Count"
                value={totalCount}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                color="success"
                icon="payments"
                label="Total Transactions Value"
                value={
                  <>
                    {totalValue}{" "}
                    <MDTypography component="span" variant="caption" fontWeight="bold" color="text">
                      AED
                    </MDTypography>
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                color="success"
                icon="check_circle"
                label="Authorized"
                value={getStatusCount("AUTHORIZED")}
                trendLabel={
                  totalCount > 0
                    ? `${Math.round((getStatusCount("AUTHORIZED") / totalCount) * 100)}% success rate`
                    : undefined
                }
                trendTone="up"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard
                color="error"
                icon="error"
                label="Failed"
                value={getStatusCount("FAILED")}
                trendLabel={getStatusCount("FAILED") === 0 ? "No failures" : undefined}
              />
            </Grid>
          </Grid>

          {/* Charts */}
          {chartData && (
            <MDBox mt={4.5}>
              <TransactionsTrendCard
                dateRangeLabel={dateRangeLabel}
                countChart={chartData.countTable}
                amountChart={chartData.amountTable}
              />
            </MDBox>
          )}
        </MDBox>
      ) : (
        <EmptyState
          icon="lock"
          title="No access"
          description="You do not have permission to view this resource."
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
